import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ASSET_CONDITIONS,
  ASSET_USAGES,
  assetsApi,
  type AssetCondition,
  type AssetSummary,
  type AssetUsage,
} from "../api/assets";
import { assetTypesApi } from "../api/catalog";
import { locationsApi } from "../api/locations";
import { buildQuery, type ApiError } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import { scannedAssetCode } from "../lib/assetCode";
import { SERVER_ALL_LIMIT, TableFooter, readPageSize, requestPageSize, storePageSize, type PageSize } from "../components/TableFooter";
import { SortableTh, sortParam, type SortState } from "../components/SortableTh";
import { Select, type SelectOption } from "../components/Select";
import { AssetTypeCombobox, type AssetTypeRef } from "../components/AssetTypeCombobox";
import { ConditionChip, UsageChip, conditionLabel, usageLabel } from "../components/ConditionChip";
import { PermLink } from "../components/PermLink";
import { AssetFormModal } from "../components/AssetFormModal";
import { QrScanner } from "../components/QrScannerLazy";
import { ExportButton } from "../components/ExportButton";
import { ReplacementBadge } from "../components/AssetSummaryCard";
import { flattenLocations, locationSelectOptions, type FlatLocation } from "../lib/locationsFlat";
import { useLocationKinds } from "../lib/useLocationKinds";
import { LocationKindIcon } from "../components/LocationKindIcon";
import { shortPathLabel } from "../lib/paths";
import Ic from "../components/icons.jsx";

const PAGE_SIZE_KEY = "bkm.assets.pageSize";
type AssetSortField = "inventoryNo" | "tag" | "manufacturer" | "condition";
const ALL = "";

/** `/asseti` — asset register: filter bar + server-side paginated table (contract §7). */
export default function AssetsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canCreate = hasPermission("assets:create");
  const canExport = hasPermission("assets:export");
  // Filters that draw on neighbouring modules appear only with their view (audit 2026-09-23 §B2).
  const canFilterTypes = hasPermission("types:view");
  const canFilterLocations = hasPermission("locations:view");
  const [params, setParams] = useSearchParams();

  const [q, setQ] = useState(params.get("q") ?? "");
  const [debouncedQ, setDebouncedQ] = useState(q.trim());
  const [type, setType] = useState<AssetTypeRef | null>(null);
  const [condition, setCondition] = useState<string>(params.get("condition") ?? ALL);
  const [usage, setUsage] = useState<string>(params.get("usage") ?? ALL);
  const [locationId, setLocationId] = useState(params.get("locationId") ?? ALL);
  const [reminderDue, setReminderDue] = useState(params.get("reminderDue") === "true");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState<PageSize>(() => readPageSize(PAGE_SIZE_KEY, 20));
  const [sort, setSort] = useState<SortState<AssetSortField>>({ field: "tag", dir: "asc" });

  const [items, setItems] = useState<AssetSummary[] | null>(null);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [locations, setLocations] = useState<FlatLocation[]>([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [scanner, setScanner] = useState(false);

  useEffect(() => {
    const h = setTimeout(() => setDebouncedQ(q.trim()), 250);
    return () => clearTimeout(h);
  }, [q]);

  useEffect(() => {
    if (!canFilterLocations) return;
    locationsApi.tree().then((tree) => setLocations(flattenLocations(tree))).catch(() => setLocations([]));
  }, [canFilterLocations]);

  // A `?typeId=` deep link (e.g. from the part search) is resolved once into the combobox value.
  const typeIdParam = params.get("typeId");
  const typeParamApplied = useRef(false);
  useEffect(() => {
    if (!typeIdParam || typeParamApplied.current) return;
    typeParamApplied.current = true;
    let cancelled = false;
    assetTypesApi.get(typeIdParam)
      .then((x) => { if (!cancelled) setType({ id: x.id, code: x.code, name: x.name }); })
      .catch(() => { if (!cancelled) setType({ id: typeIdParam, code: "", name: "" }); });
    return () => { cancelled = true; };
  }, [typeIdParam]);

  // Mirror the filters into the URL so the list survives back navigation.
  useEffect(() => {
    const next = new URLSearchParams();
    if (debouncedQ) next.set("q", debouncedQ);
    if (type) next.set("typeId", type.id);
    if (condition) next.set("condition", condition);
    if (usage) next.set("usage", usage);
    if (locationId) next.set("locationId", locationId);
    if (reminderDue) next.set("reminderDue", "true");
    setParams(next, { replace: true });
  }, [debouncedQ, type, condition, usage, locationId, reminderDue, setParams]);

  const filterKey = `${debouncedQ}|${type?.id ?? ""}|${condition}|${usage}|${locationId}|${reminderDue}|${sortParam(sort)}|${pageSize}`;
  const prevFilterKey = useRef(filterKey);

  useEffect(() => {
    if (prevFilterKey.current !== filterKey) {
      prevFilterKey.current = filterKey;
      if (page !== 0) { setPage(0); return; }
    }
    let cancelled = false;
    setError(null);
    assetsApi
      .list({
        q: debouncedQ || undefined,
        typeId: type?.id,
        condition: (condition || undefined) as AssetCondition | undefined,
        usage: (usage || undefined) as AssetUsage | undefined,
        locationId: locationId || undefined,
        reminderDue: reminderDue ? true : undefined,
        page,
        size: requestPageSize(pageSize),
        sort: sortParam(sort),
      })
      .then((res) => {
        if (cancelled) return;
        setItems(res.content);
        setTotal(res.totalElements);
      })
      .catch((err: ApiError) => {
        if (cancelled) return;
        setError(err.message);
        setItems([]);
      });
    return () => { cancelled = true; };
    // filterKey already encodes every filter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey, page]);

  const conditionOptions = useMemo<SelectOption<string>[]>(
    () => [{ value: ALL, label: t("assets.filter.allConditions", "Sva stanja") }, ...ASSET_CONDITIONS.map((c) => ({ value: c, label: conditionLabel(t, c) }))],
    [t],
  );
  const usageOptions = useMemo<SelectOption<string>[]>(
    () => [{ value: ALL, label: t("assets.filter.allUsages", "Montirani i na lageru") }, ...ASSET_USAGES.map((u) => ({ value: u, label: usageLabel(t, u) }))],
    [t],
  );
  const { iconOf } = useLocationKinds();
  const locationOptions = useMemo<SelectOption<string>[]>(
    () => [{ value: ALL, label: t("assets.filter.allLocations", "Sve lokacije") }, ...locationSelectOptions(locations, { left: (e) => <LocationKindIcon icon={iconOf(e.kind)} /> })],
    [locations, t, iconOf],
  );

  // The export takes what the list shows — same filters and order — but every row, not one page.
  const exportPath = `/exports/assets.xlsx${buildQuery({
    q: debouncedQ || undefined,
    typeId: type?.id,
    condition: condition || undefined,
    usage: usage || undefined,
    locationId: locationId || undefined,
    reminderDue: reminderDue ? true : undefined,
    sort: sortParam(sort),
  })}`;

  const hasFilters = debouncedQ !== "" || !!type || condition !== ALL || usage !== ALL || locationId !== ALL || reminderDue;

  function clearFilters() {
    setQ(""); setType(null); setCondition(ALL); setUsage(ALL); setLocationId(ALL); setReminderDue(false);
  }

  return (
    <div>
      <div className="page-head">
        <div>
          <h1>{t("assets.title", "Asseti")}</h1>
          <div className="sub">
            {items === null
              ? t("common.loading", "Učitavanje…")
              : t("assets.count", "{{count}} asseta u registru", { count: total })}
          </div>
        </div>
        <div className="page-actions">
          <button type="button" className="btn lg" onClick={() => setScanner(true)}>
            <Ic.selector /> {t("assets.scanQr", "Skeniraj QR")}
          </button>
          {canExport && (
            <ExportButton path={exportPath} label={t("assets.export", "Izvezi u Excel")} filename="asseti.xlsx" />
          )}
          {canCreate && (
            <button type="button" className="btn primary lg" onClick={() => setCreateOpen(true)}>
              <Ic.plus /> {t("assets.new", "Novi asset")}
            </button>
          )}
        </div>
      </div>

      <div className="filter-bar">
        <div className="search-wrap">
          <Ic.search aria-hidden="true" />
          <input
            className="input"
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("assets.filter.searchPlaceholder", "ID, oznaka, serijski, proizvođač, šifra ili naziv tipa…")}
            aria-label={t("action.search", "Pretraga")}
          />
        </div>
        {canFilterTypes && (
          <div style={{ flex: "0 1 260px", minWidth: 200 }}>
            <AssetTypeCombobox value={type} onChange={setType} placeholder={t("assets.filter.type", "Tip dela…")} />
          </div>
        )}
        <Select<string> value={condition} options={conditionOptions} onChange={setCondition} ariaLabel={t("assets.field.condition", "Stanje")} style={{ flex: "0 1 160px", minWidth: 140 }} />
        <Select<string> value={usage} options={usageOptions} onChange={setUsage} ariaLabel={t("assets.field.usage", "Upotreba")} style={{ flex: "0 1 200px", minWidth: 160 }} />
        {canFilterLocations && (
          <Select<string> value={locationId} options={locationOptions} onChange={setLocationId} ariaLabel={t("assets.field.location", "Fizička lokacija")} style={{ flex: "0 1 240px", minWidth: 180 }} />
        )}
        <button
          type="button"
          className={`chip${reminderDue ? " active" : ""}`}
          aria-pressed={reminderDue}
          onClick={() => setReminderDue((v) => !v)}
          style={{ cursor: "pointer" }}
        >
          <Ic.alert /> {t("assets.filter.reminderDue", "Dospeli podsetnici")}
        </button>
        {hasFilters && (
          <button type="button" className="btn sm ghost" onClick={clearFilters}>{t("action.clear", "Očisti")}</button>
        )}
      </div>

      {error && <div style={errorBox} role="alert">{error}</div>}

      <div className="card">
        {items === null ? (
          <div className="empty">{t("common.loading", "Učitavanje…")}</div>
        ) : items.length === 0 ? (
          <div className="empty">
            {hasFilters
              ? t("assets.emptyFiltered", "Nijedan asset ne odgovara filterima.")
              : t("assets.empty", "Registar je prazan. Dodajte prvi asset.")}
          </div>
        ) : (
          <div className="tbl-wrap">
            <table className="tbl list-card compact">
              <thead>
                <tr>
                  <SortableTh field="inventoryNo" sort={sort} onSort={setSort} className="hide-tablet">{t("assets.col.inventoryNo", "ID")}</SortableTh>
                  <SortableTh field="tag" sort={sort} onSort={setSort}>{t("assets.col.tag", "Oznaka")}</SortableTh>
                  <th>{t("assets.col.type", "Tip")}</th>
                  <SortableTh field="manufacturer" sort={sort} onSort={setSort} className="hide-tablet">{t("assets.col.manufacturer", "Proizvođač")}</SortableTh>
                  <SortableTh field="condition" sort={sort} onSort={setSort}>{t("assets.col.condition", "Stanje")}</SortableTh>
                  <th className="hide-tablet">{t("assets.col.usage", "Upotreba")}</th>
                  <th>{t("assets.col.position", "Logička pozicija")}</th>
                  <th>{t("assets.col.location", "Fizička lokacija")}</th>
                  <th>{t("assets.col.reminder", "Podsetnik")}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((a) => {
                  const pos = a.positionId ? shortPathLabel(a.positionPath, a.positionName) : null;
                  return (
                    <tr
                      key={a.id}
                      className="is-clickable"
                      onClick={() => navigate(`/asseti/${a.id}`)}
                      onKeyDown={(e) => { if (e.key === "Enter") navigate(`/asseti/${a.id}`); }}
                      tabIndex={0}
                    >
                      <td className="mono hide-tablet" data-label={t("assets.col.inventoryNo", "ID")} style={{ color: "var(--ink-3)", fontSize: 12.5, whiteSpace: "nowrap" }}>{a.inventoryNo}</td>
                      <td className="mono" data-label={t("assets.col.tag", "Oznaka")}>
                        <Link to={`/asseti/${a.id}`} onClick={(e) => e.stopPropagation()}><b>{a.tag}</b></Link>
                        {a.serialNo && <span className="muted" style={{ display: "block", fontSize: 11.5 }}>{a.serialNo}</span>}
                      </td>
                      <td data-label={t("assets.col.type", "Tip")}>
                        <span className="mono" style={{ fontSize: 12, color: "var(--ink-3)" }}>{a.typeCode}</span> {a.typeName}
                      </td>
                      <td className="hide-tablet" data-label={t("assets.col.manufacturer", "Proizvođač")}>
                        {a.manufacturer ?? (a.isCustomMade ? null : <span className="muted">—</span>)}
                        {a.manufacturerCode && <span className="muted" style={{ display: "block", fontSize: 11.5 }}>{a.manufacturerCode}</span>}
                        {a.isCustomMade && <span className="tag info">{t("assets.customMadeTag", "sopstvena izrada")}</span>}
                      </td>
                      <td data-label={t("assets.col.condition", "Stanje")}><ConditionChip condition={a.condition} /></td>
                      <td className="hide-tablet" data-label={t("assets.col.usage", "Upotreba")}><UsageChip usage={a.usage} /></td>
                      <td data-label={t("assets.col.position", "Logička pozicija")}>
                        {pos && a.positionId
                          ? <PermLink permission="structure:view" to={`/struktura/${a.positionId}`} title={pos.full} onClick={(e) => e.stopPropagation()}>{pos.short}</PermLink>
                          : (
                            <>
                              {/* Tablets hide the Upotreba column; "Na lageru" moves in here instead of the dash. */}
                              <span className="muted show-tablet">{usageLabel(t, a.usage)}</span>
                              <span className="muted hide-tablet">—</span>
                            </>
                          )}
                      </td>
                      <td data-label={t("assets.col.location", "Fizička lokacija")}>
                        {a.effectiveLocation ? <span title={a.effectiveLocation.code}>{a.effectiveLocation.name}</span> : <span className="muted">—</span>}
                        {a.locationNote && <span className="muted" style={{ display: "block", fontSize: 11.5 }}>{a.locationNote}</span>}
                      </td>
                      <td data-label={t("assets.col.reminder", "Podsetnik")}>
                        <ReplacementBadge asset={a} compact />
                        {a.usage !== "MONTIRAN" && a.expectedReturnAt && (
                          <span className="muted" style={{ fontSize: 12 }}>{t("assets.returnShort", "Povratak {{date}}", { date: a.expectedReturnAt.split("-").reverse().join(".") })}</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <TableFooter
        page={page}
        pageSize={pageSize}
        total={total}
        onPage={setPage}
        onPageSize={(s) => { storePageSize(PAGE_SIZE_KEY, s); setPageSize(s); }}
        allLimit={SERVER_ALL_LIMIT}
      />

      {createOpen && (
        <AssetFormModal
          mode={{ kind: "create", defaultTypeId: type && type.name ? type : null }}
          onClose={() => setCreateOpen(false)}
          onSaved={(saved) => { setCreateOpen(false); navigate(`/asseti/${saved.id}`); }}
        />
      )}
      {scanner && (
        <QrScanner onClose={() => setScanner(false)} onResult={(tag) => { setScanner(false); navigate(`/a/${encodeURIComponent(scannedAssetCode(tag))}`); }} />
      )}
    </div>
  );
}

const errorBox: CSSProperties = { padding: 10, background: "rgba(182,53,58,.08)", color: "var(--err)", borderRadius: 6, fontSize: 13, marginBottom: 12 };
